import { create } from 'zustand';
import type { Design, DesignRecord, Widget } from '@noorview/core';
import { normalizeDesign } from '@noorview/core';
import { backend } from '../data/current';
import { ConflictError } from '../data/backend';

/**
 * Studio editing state. Every edit goes through change() (one undo step) or preview() (live while dragging, committed
 * as one step on release). Drafts save automatically a moment after you stop editing, with conflict detection.
 */

const HISTORY = 150;
const SAVE_DELAY = 700;

export type SaveState = 'saved' | 'unsaved' | 'saving' | 'error' | 'conflict';

interface StudioState {
  designId: string | null;
  revision: number;
  design: Design | null;
  past: Design[];
  future: Design[];
  selected: string | null;
  save: SaveState;
  savedAt: string | null;
  conflict: DesignRecord | null;
  coalesceKey: string | null;
  coalesceAt: number;
  dragBase: Design | null;

  open: (rec: DesignRecord) => void;
  change: (fn: (d: Design) => Design, coalesce?: string) => void;
  /** Live update without an undo step (used while dragging). */
  preview: (fn: (d: Design) => Design) => void;
  beginDrag: () => void;
  endDrag: () => void;
  cancelDrag: () => void;
  undo: () => void;
  redo: () => void;
  select: (id: string | null) => void;
  updateWidget: (id: string, patch: Partial<Widget> | ((w: Widget) => Partial<Widget>), coalesce?: string) => void;
  flush: () => Promise<boolean>;
  resolveConflict: (keep: 'mine' | 'theirs') => Promise<void>;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let saving: Promise<boolean> | null = null;

export const useStudio = create<StudioState>((set, get) => {
  const scheduleSave = () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => { timer = null; void get().flush(); }, SAVE_DELAY);
  };
  const commit = (next: Design, prev: Design, coalesce?: string) => {
    const s = get();
    const now = Date.now();
    const merge = !!coalesce && s.coalesceKey === coalesce && now - s.coalesceAt < 1200;
    set({
      design: next,
      past: merge ? s.past : [...s.past.slice(-HISTORY + 1), prev],
      future: [],
      save: s.save === 'conflict' ? 'conflict' : 'unsaved',
      coalesceKey: coalesce ?? null,
      coalesceAt: now,
    });
    scheduleSave();
  };

  // What this Studio last saved for each design. Reopening a design straight after leaving it (the blank canvas and a
  // screen, back and forth) can come before the masjid's data has caught up with that save; this keeps the newer one.
  const lastSaved = new Map<string, { revision: number; design: Design; at: string }>();

  return {
    designId: null, revision: 0, design: null, past: [], future: [], selected: null, save: 'saved', savedAt: null, conflict: null,
    coalesceKey: null, coalesceAt: 0, dragBase: null,

    open: (rec) => {
      if (timer) { clearTimeout(timer); timer = null; }
      const mine = lastSaved.get(rec.id);
      const newer = mine && mine.revision > rec.draftRevision ? mine : null;
      set({ designId: rec.id, revision: newer?.revision ?? rec.draftRevision, design: newer ? newer.design : normalizeDesign(rec.draft), past: [], future: [], selected: null, save: 'saved', savedAt: newer?.at ?? rec.draftUpdatedAt, conflict: null, coalesceKey: null, dragBase: null });
    },
    change: (fn, coalesce) => {
      const d = get().design;
      if (!d) return;
      const next = fn(d);
      if (next === d) return;
      commit(next, d, coalesce);
    },
    preview: (fn) => {
      const d = get().design;
      if (!d) return;
      const next = fn(d);
      if (next !== d) set({ design: next });
    },
    beginDrag: () => set({ dragBase: get().design }),
    endDrag: () => {
      const { dragBase, design } = get();
      set({ dragBase: null });
      if (dragBase && design && design !== dragBase && JSON.stringify(dragBase.widgets) !== JSON.stringify(design.widgets)) commit(design, dragBase);
      else if (dragBase) set({ design: dragBase });
    },
    cancelDrag: () => { const b = get().dragBase; if (b) set({ design: b, dragBase: null }); },
    undo: () => {
      const { past, design, future } = get();
      if (!past.length || !design) return;
      const prev = past[past.length - 1]!;
      set({ design: prev, past: past.slice(0, -1), future: [design, ...future].slice(0, HISTORY), save: 'unsaved', coalesceKey: null });
      if (get().selected && !prev.widgets.some((w) => w.id === get().selected)) set({ selected: null });
      scheduleSave();
    },
    redo: () => {
      const { past, design, future } = get();
      if (!future.length || !design) return;
      const next = future[0]!;
      set({ design: next, past: [...past, design].slice(-HISTORY), future: future.slice(1), save: 'unsaved', coalesceKey: null });
      scheduleSave();
    },
    select: (id) => set({ selected: id }),
    updateWidget: (id, patch, coalesce) => get().change((d) => ({
      ...d, widgets: d.widgets.map((w) => (w.id === id ? { ...w, ...(typeof patch === 'function' ? patch(w) : patch) } : w)),
    }), coalesce ? `${id}:${coalesce}` : undefined),

    flush: async () => {
      if (timer) { clearTimeout(timer); timer = null; }
      if (saving) await saving;
      const s = get();
      if (!s.designId || !s.design || s.save === 'saved' || s.save === 'conflict') return s.save !== 'conflict';
      const { designId, design, revision } = s;
      set({ save: 'saving' });
      saving = (async () => {
        try {
          const r = await backend.saveDraft(designId, design, revision);
          lastSaved.set(designId, { revision: r.revision, design, at: r.savedAt });
          const cur = get();
          if (cur.designId !== designId) return true;
          set({ revision: r.revision, savedAt: r.savedAt, save: cur.design === design ? 'saved' : 'unsaved' });
          if (cur.design !== design) scheduleSave();
          return true;
        } catch (e) {
          if (e instanceof ConflictError) { set({ save: 'conflict', conflict: e.latest }); return false; }
          set({ save: 'error' });
          return false;
        } finally { saving = null; }
      })();
      return saving;
    },

    resolveConflict: async (keep) => {
      const { conflict, design, designId } = get();
      if (!conflict || !designId) return;
      if (keep === 'theirs') { get().open(conflict); return; }
      set({ save: 'saving' });
      try {
        const r = await backend.saveDraft(designId, design!, conflict.draftRevision, true);
        lastSaved.set(designId, { revision: r.revision, design: design!, at: r.savedAt });
        set({ revision: r.revision, savedAt: r.savedAt, save: 'saved', conflict: null });
      } catch { set({ save: 'error' }); }
    },
  };
});

export const selectedWidget = (s: Pick<StudioState, 'design' | 'selected'>) => s.design?.widgets.find((w) => w.id === s.selected) ?? null;
